/**
 * Multilingual route map for Spain Luxury Rentals.
 * Each entry maps a "page key" to its localized URL path.
 */

export const LANGS = ["en", "es", "ru", "fr", "de", "it", "nl", "pl"];
export const LANG_LABELS = {
  en: "English",
  es: "Español",
  ru: "Русский",
  fr: "Français",
  de: "Deutsch",
  it: "Italiano",
  nl: "Nederlands",
  pl: "Polski",
};
export const DEFAULT_LANG = "en";
// International languages added in Prompt Final Global. They mirror EN
// paths under /fr/*, /de/*, /it/*, /nl/*, /pl/* — content falls back to EN
// until a native translation is published.
export const NEW_LANGS = ["fr", "de", "it", "nl", "pl"];

export const ROUTE_KEYS = {
  HOME: "home",
  CATALOG: "catalog",
  CAR_DETAIL: "car_detail",
  ZONE_LANDING: "zone_landing",
  GUIDES: "guides",
  GUIDE_DETAIL: "guide_detail",
  RENTAL_FAQ: "rental_faq",
  ROUTES: "routes",
  ROUTE_DETAIL: "route_detail",
  CONTACT: "contact",
  RENTAL_CONDITIONS: "rental_conditions",
  TERMS: "terms",
  PRIVACY: "privacy",
  COOKIES: "cookies",
};

/** Static page paths per language (NO trailing slash, with leading slash). */
const NEW_LANG_PREFIX = (lang, enPath) => `/${lang}${enPath === "/" ? "" : enPath}`;
const _mk = (enPath, esPath, ruPath) => ({
  en: enPath, es: esPath, ru: ruPath,
  fr: NEW_LANG_PREFIX("fr", enPath), de: NEW_LANG_PREFIX("de", enPath),
  it: NEW_LANG_PREFIX("it", enPath), nl: NEW_LANG_PREFIX("nl", enPath),
  pl: NEW_LANG_PREFIX("pl", enPath),
});
export const ROUTE_MAP = {
  [ROUTE_KEYS.HOME]: { en: "/", es: "/es", ru: "/ru", fr: "/fr", de: "/de", it: "/it", nl: "/nl", pl: "/pl" },
  [ROUTE_KEYS.CATALOG]: _mk("/cars", "/es/coches", "/ru/cars"),
  [ROUTE_KEYS.ROUTES]: _mk("/routes", "/es/rutas", "/ru/routes"),
  [ROUTE_KEYS.CONTACT]: _mk("/contact", "/es/contacto", "/ru/contact"),
  [ROUTE_KEYS.RENTAL_CONDITIONS]: _mk("/rental-conditions", "/es/condiciones-alquiler", "/ru/rental-conditions"),
  [ROUTE_KEYS.TERMS]: _mk("/terms-and-conditions", "/es/terminos-condiciones", "/ru/terms-and-conditions"),
  [ROUTE_KEYS.PRIVACY]: _mk("/privacy-policy", "/es/politica-privacidad", "/ru/privacy-policy"),
  [ROUTE_KEYS.COOKIES]: _mk("/cookies-policy", "/es/politica-cookies", "/ru/cookies-policy"),
  [ROUTE_KEYS.GUIDES]: _mk("/guides", "/es/guias", "/ru/guides"),
  [ROUTE_KEYS.RENTAL_FAQ]: _mk("/rental-faq", "/es/preguntas-frecuentes-alquiler", "/ru/rental-faq"),
};

export const GUIDE_BASES = {
  en: "/guides", es: "/es/guias", ru: "/ru/guides",
  fr: "/fr/guides", de: "/de/guides", it: "/it/guides",
  nl: "/nl/guides", pl: "/pl/guides",
};

/** Route base paths for individual route pages. */
export const ROUTE_BASES = {
  en: "/routes", es: "/es/rutas", ru: "/ru/routes",
  fr: "/fr/routes", de: "/de/routes", it: "/it/routes",
  nl: "/nl/routes", pl: "/pl/routes",
};

/** Car detail base paths (Prompt 9). */
export const CAR_BASES = {
  en: "/cars", es: "/es/coches", ru: "/ru/cars",
  fr: "/fr/cars", de: "/de/cars", it: "/it/cars",
  nl: "/nl/cars", pl: "/pl/cars",
};

/** Resolve language and route-key from the current pathname. */
export function detectLangAndKey(pathname) {
  const p = pathname.replace(/\/+$/, "") || "/";

  // Guide detail / index
  for (const lang of LANGS) {
    const base = GUIDE_BASES[lang];
    if (p === base) return { lang, key: ROUTE_KEYS.GUIDES, slug: null };
    if (p.startsWith(base + "/")) {
      return { lang, key: ROUTE_KEYS.GUIDE_DETAIL, slug: p.substring(base.length + 1) };
    }
  }

  // Car detail / catalog pages (Prompt 9)
  for (const lang of LANGS) {
    const base = CAR_BASES[lang];
    if (p === base) return { lang, key: ROUTE_KEYS.CATALOG, slug: null };
    if (p.startsWith(base + "/")) {
      return { lang, key: ROUTE_KEYS.CAR_DETAIL, slug: p.substring(base.length + 1) };
    }
  }

  // Route detail pages
  for (const lang of LANGS) {
    const base = ROUTE_BASES[lang];
    if (p === base) return { lang, key: ROUTE_KEYS.ROUTES, slug: null };
    if (p.startsWith(base + "/")) {
      return { lang, key: ROUTE_KEYS.ROUTE_DETAIL, slug: p.substring(base.length + 1) };
    }
  }
  // Static pages
  for (const [key, paths] of Object.entries(ROUTE_MAP)) {
    for (const lang of LANGS) {
      if (paths[lang] === p) return { lang, key, slug: null };
    }
  }
  // Default fallback by prefix
  for (const nlang of ["es", "ru", "fr", "de", "it", "nl", "pl"]) {
    if (p === `/${nlang}` || p.startsWith(`/${nlang}/`)) return { lang: nlang, key: null, slug: null };
  }
  return { lang: "en", key: null, slug: null };
}

/** Build the equivalent URL in a target language for the given current pathname. */
export function buildEquivalentPath(pathname, targetLang) {
  const { lang: currentLang, key, slug } = detectLangAndKey(pathname);
  if (key === ROUTE_KEYS.CAR_DETAIL && slug) {
    return `${CAR_BASES[targetLang]}/${slug}`;
  }
  if (key === ROUTE_KEYS.ROUTE_DETAIL && slug) {
    return `${ROUTE_BASES[targetLang]}/${slug}`;
  }
  if (key === ROUTE_KEYS.GUIDE_DETAIL && slug) {
    return `${GUIDE_BASES[targetLang]}/${slug}`;
  }
  if (key && ROUTE_MAP[key]) {
    return ROUTE_MAP[key][targetLang];
  }
  // Last-resort fallback: keep the path but swap the language prefix instead
  // of dumping the user on the home page (Prompt 12 §3).
  const p = pathname.replace(/\/+$/, "") || "/";
  const stripped =
    currentLang === "en" ? p :
    p.replace(new RegExp(`^/${currentLang}`), "") || "/";
  const tail = stripped === "" ? "/" : stripped;
  if (targetLang === "en") return tail;
  return `/${targetLang}${tail === "/" ? "" : tail}`;
}

/** Helper to build a path with the current language context. */
export function pathFor(key, lang) {
  return ROUTE_MAP[key]?.[lang] || ROUTE_MAP[key]?.en || "/";
}

export function routeDetailPath(slug, lang) {
  return `${ROUTE_BASES[lang]}/${slug}`;
}

export function guideDetailPath(slug, lang) {
  return `${GUIDE_BASES[lang]}/${slug}`;
}

export function carDetailPath(slug, lang) {
  return `${CAR_BASES[lang]}/${slug}`;
}
